import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export type OnboardingStep =
  | "welcome"
  | "hubspot_connection"
  | "stripe_connection"
  | "customer_definition"
  | "contract_creation"
  | "custom_objects"
  | "renewal_settings"
  | "subscription_creation"
  | "daily_email"
  | "review";

export interface OnboardingSettings {
  customerLifecycleStage?: string | null;
  netNewDealPipelineId?: string | null;
  netNewDealStageId?: string | null;
  autoCreateSubscriptions?: boolean;
  subscriptionProductId?: string | null;
  subscriptionProductMapping?: {
    enabled?: boolean;
    defaultProductId?: string;
    rules?: Array<Record<string, unknown>>;
  };
  subscriptionSetup?: {
    fieldMappings?: {
      startDate?: { field?: string };
      endDate?: { field?: string };
    };
  };
  enableRenewalDeals?: boolean;
  renewalPipelineId?: string | null;
  renewalStageId?: string | null;
  renewalDaysBeforeExpiration?: number;
  autoCreateOnSubscription?: boolean;
  autoCreateDaysAway?: number;
  syncContractsToHubSpot?: boolean;
  autoSyncFieldMappings?: boolean;
  uiExtensionEnabled?: boolean;
  cSuiteDailyReportEnabled?: boolean;
}

export interface OnboardingState {
  step: OnboardingStep;
  hubspotConnected: boolean;
  stripeConnected: boolean;
  settings: OnboardingSettings;
  stripeProducts?: Array<{
    id: string;
    name: string;
    description?: string;
    active: boolean;
    defaultPriceId?: string;
    default_price?:
      | string
      | {
          id: string;
          active: boolean;
        };
  }>;
  subscriptionProductMapping?: {
    enabled?: boolean;
    defaultProductId?: string;
    rules?: Array<Record<string, unknown>>;
  };
  subscriptionBilling?: {
    collectionMethod?: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
    prorationBehavior?: "none" | "create_prorations" | "always_invoice";
  } | null;
  subscriptionSetup?: {
    fieldMappings?: {
      startDate?: { field?: string };
      endDate?: { field?: string };
    };
  };
  dealDateFields?: Array<{
    name: string;
    label: string;
    fieldType: "date" | "datetime";
  }>;
  lifecycleStages?: Array<{ value: string; label: string }>;
  pipelines?: Array<{ id: string; label: string }>;
  aiSuggestions?: Record<string, unknown>;
}

/** Explicit right panel payload from backend (GET state / POST action) */
export interface OnboardingRightPanelData {
  component: string;
  show: boolean;
  stripeProducts?: Array<{
    id: string;
    name: string;
    description?: string;
    active?: boolean;
    defaultPriceId?: string;
    default_price?:
      | string
      | { id: string; active: boolean };
  }>;
  dealDateFields?: Array<{
    name: string;
    label: string;
    fieldType?: "date" | "datetime";
  }>;
  subscriptionBilling?: {
    collectionMethod?: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
    prorationBehavior?: "none" | "create_prorations" | "always_invoice";
  };
}

export interface SubscriptionStepCta {
  confirmAllowed: boolean;
  primaryCta: {
    label: string;
    action: string;
    value?: string;
  };
}

export interface OnboardingButton {
  id: string;
  label: string;
  action: string;
  value?: string;
  style: "primary" | "secondary" | string;
}

export interface ChatMessage {
  id: string;
  type: "assistant" | "user" | "system";
  content: string;
  buttons?: OnboardingButton[];
  timestamp: string;
  metadata?: {
    step?: OnboardingStep;
    showInRightPanel?: boolean;
    rightPanelComponent?: string;
  };
}

export interface GetOnboardingStateData {
  state: OnboardingState;
  currentMessage: ChatMessage;
  messages?: ChatMessage[];
  rightPanel?: OnboardingRightPanelData;
  subscriptionStepCta?: SubscriptionStepCta;
}

export interface HandleActionData {
  success: boolean;
  nextState: OnboardingState;
  nextMessage: ChatMessage;
  messages?: ChatMessage[];
  updatedMessages?: ChatMessage[];
  connectEndpoint?: string;
  rightPanel?: OnboardingRightPanelData;
  subscriptionStepCta?: SubscriptionStepCta;
}

export interface SetValueData {
  success: boolean;
  nextState: OnboardingState;
  nextMessage: ChatMessage;
  messages?: ChatMessage[];
}

export interface PipelineStagesData {
  stages: Array<{ id: string; label: string }>;
}

export interface PipelinesData {
  pipelines: Array<{ id: string; label: string }>;
}

export class OnboardingService {
  async getOnboardingState(): Promise<ApiResponse<GetOnboardingStateData>> {
    return await httpService.get<ApiResponse<GetOnboardingStateData>>(
      "/revsync/onboarding/state",
    );
  }

  async handleAction(
    action: string,
    value?: string,
  ): Promise<ApiResponse<HandleActionData>> {
    return await httpService.post<ApiResponse<HandleActionData>>(
      "/revsync/onboarding/action",
      { action, value },
    );
  }

  async setValue(
    field: string,
    value: any,
  ): Promise<ApiResponse<SetValueData>> {
    return await httpService.put<ApiResponse<SetValueData>>(
      "/revsync/onboarding/value",
      { field, value },
    );
  }

  async getPipelineStages(
    pipelineId: string,
  ): Promise<ApiResponse<PipelineStagesData>> {
    return await httpService.get<ApiResponse<PipelineStagesData>>(
      `/revsync/onboarding/pipelines/${pipelineId}/stages`,
    );
  }

  async getPipelines(): Promise<ApiResponse<PipelinesData>> {
    return await httpService.get<ApiResponse<PipelinesData>>(
      "/revsync/onboarding/pipelines",
    );
  }
}

export const onboardingService = new OnboardingService();
